// Utils
import { t } from 'i18next';
import cn from '../../../utils/cn';

// Components
import BannerHeaderActionRow from '../../molecules/BannerHeaderActionRow/BannerHeaderActionRow';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';

// Styles
import styles from './BannerHeader.module.css';
import bannerContentRowStyles from '../../molecules/BannerContentRow/BannerContentRow.module.css';
import Button from '../../atoms/Button/Button';

/**
 * BannerHeader component - A reusable banner header with title, actions and content
 * @param {object} props - Component props
 * @param {string | React.ReactNode} [props.title] - Title text, or a composed title node (e.g. menu trigger)
 * @param {keyof typeof import('../../atoms/Picto/Picto').availablePictos} [props.rightActionIcon] - Icon to display with the butotn on the right of the row
 * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} [props.onRightActionClick] - Function called when the right action button is clicked
 * @param {React.ReactNode} [props.leftActionComponent] - Left action component
 * @param {React.ReactNode} [props.navigationComponent] - Navigation component
 * @param {React.ReactNode} [props.titleAction] - Action rendered after the title (e.g. menu trigger)
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} [props.children] - Child elements
 * @param {boolean} [props.isLoading=false] - Loading state to display skeletons
 * @returns {JSX.Element}
 */
function BannerHeader({
  title,
  className,
  rightActionIcon,
  onRightActionClick,
  leftActionComponent,
  navigationComponent,
  titleAction,
  children,
  isLoading = false,
}) {
  if (isLoading) {
    return (
      <>
        <BannerHeaderActionRow
          title=" "
          rightActionButton={null}
        >
          <Skeleton shape="rectangle" width="32px" height="32px" />
          <Skeleton shape="rectangle" width="200px" height="32px" style={{ marginLeft: '16px' }} />
          <Skeleton shape="rectangle" width="32px" height="32px" />
        </BannerHeaderActionRow>
        <div
          id="banner-header-content"
          className={cn([styles['banner-header'], className])}
        >
          <div className={bannerContentRowStyles['banner-content-row']}>
            {[1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className={`${bannerContentRowStyles['fieldset-container']} ${bannerContentRowStyles['content-item']}`}
              >
                <Skeleton shape="rectangle" width="100%" height="120px" />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <BannerHeaderActionRow
        title={title}
        titleAction={titleAction}
        rightActionButton={(
          onRightActionClick ? (
            <Button
              icon={rightActionIcon}
              iconPosition="right"
              color="no-background"
              onClick={onRightActionClick}
            >
              {t('detail.banner.collapse')}
            </Button>
          ) : null
        )}
      >
        {leftActionComponent}
        {navigationComponent}
      </BannerHeaderActionRow>
      <div
        id="banner-header-content"
        className={cn([styles['banner-header'], className])}
      >
        {children}
      </div>
    </>
  );
}

export default BannerHeader;
